/**
 * AI image labels
 *
 * Scans <img> elements site-wide for filename markers and overlays a
 * bottom-right badge for images generated or modified with AI:
 *   - "_AI_generated" -> LABEL_AI GENERATED
 *   - "_AI_modified"  -> LABEL_AI MODIFIED
 *   - "_AI_"          -> LABEL_AI (checked last, since the two markers
 *                        above also contain "_AI_")
 *   - "gemini/chatgpt/..." -> LABEL_AI
 *
 * Placement strategy: the badge is attached to the nearest already
 * positioned ancestor of the image (the theme wraps most meaningful
 * images in a position:relative "media"-style container already) and
 * placed with position:absolute, using a one-off pixel offset computed
 * from getBoundingClientRect() so it lines up with the image's actual
 * bottom-right corner even if the image doesn't exactly fill that
 * ancestor. Because it's position:absolute (not fixed) inside a real
 * ancestor in normal flow, it scrolls with the page natively - no
 * per-frame polling, no scroll lag. Position is only recalculated when
 * the image or its ancestor actually change size (ResizeObserver) or on
 * window resize, so there's no continuous work happening.
 *
 * If no positioned ancestor exists at all (plain inline content images),
 * the image is wrapped in a small span instead, same as before.
 *
 * Runs on initial load and watches the DOM via MutationObserver so images
 * added later (cart drawer, quick add, section re-renders, sliders, etc.)
 * get labelled too. Badge asset URLs come from a JSON config script tag
 * rendered in theme.liquid (id="ai-image-labels-config"), since Shopify
 * fingerprints asset URLs and they can't be hardcoded here.
 */
(function () {
  'use strict';

  function getConfig() {
    var el = document.getElementById('ai-image-labels-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  var CONFIG = getConfig();
  if (!CONFIG || !CONFIG.labels) return;

  // Order matters: more specific markers must be checked before the
  // generic "_ai" one, since they contain it as a substring.
  var MARKERS = [
    { key: 'generated', marker: '_ai_generated' },
    { key: 'modified', marker: '_ai_modified' },
    { key: 'ai', marker: '_ai_' },
    { key: 'ai', marker: 'chatgpt' },
    { key: 'ai', marker: 'gemini' },
    { key: 'ai', marker: 'designer_' },
    { key: 'ai', marker: 'firefly' },
    { key: 'ai', marker: 'leonardo' },
  ];

  // Badge width as a share of the image's own rendered width, clamped
  // between MIN_PX and MAX_PX. Gutter is the offset from the image's
  // right/bottom edge, in pixels.
  var WIDTH_RATIO = 0.5;
  var MIN_PX = 32;
  var MAX_PX = 120;
  var GUTTER_PX = 5;

  function getLabelKey(src) {
    if (!src) return null;

    var filename = src.split('/').pop().split('?')[0].split('#')[0];
    try {
      filename = decodeURIComponent(filename);
    } catch (e) {
      // Ignore malformed URI components and match against the raw string.
    }
    filename = filename.toLowerCase();

    for (var i = 0; i < MARKERS.length; i++) {
      if (filename.indexOf(MARKERS[i].marker) !== -1) {
        return MARKERS[i].key;
      }
    }
    return null;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function createBadge(key) {
    var badge = document.createElement('img');
    badge.className = 'ai-label-badge ai-label-badge--' + key;
    badge.src = CONFIG.labels[key];
    badge.alt = (CONFIG.altText && CONFIG.altText[key]) || '';
    badge.loading = 'lazy';
    badge.setAttribute('aria-hidden', 'true');
    return badge;
  }

  function getPositionedAncestor(el) {
    var node = el.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      var position = window.getComputedStyle(node).position;
      if (position === 'relative' || position === 'absolute' || position === 'fixed' || position === 'sticky') {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  // Positions `badge` (already position:absolute, already appended
  // inside `ancestor`) so its bottom-right corner sits GUTTER_PX inside
  // the image's own bottom-right corner. Returns false if the image has
  // no rendered size yet (nothing to position against).
  function positionInAncestor(img, ancestor, badge) {
    var imgRect = img.getBoundingClientRect();
    if (imgRect.width === 0 || imgRect.height === 0) return false;

    var ancestorRect = ancestor.getBoundingClientRect();
    var widthPx = clamp(imgRect.width * WIDTH_RATIO, MIN_PX, MAX_PX);

    var right = ancestorRect.right - imgRect.right + GUTTER_PX;
    var bottom = ancestorRect.bottom - imgRect.bottom + GUTTER_PX;

    badge.style.width = widthPx + 'px';
    badge.style.right = right + 'px';
    badge.style.bottom = bottom + 'px';
    return true;
  }

  function attachToAncestor(img, ancestor, key) {
    var badge = createBadge(key);
    badge.style.position = 'absolute';
    ancestor.appendChild(badge);

    var update = function () {
      positionInAncestor(img, ancestor, badge);
    };

    update();
    // Layout can still settle shortly after insertion (webfonts, other
    // deferred scripts, lazy images finishing their first layout) - a
    // couple of follow-up passes catch that without ongoing polling.
    requestAnimationFrame(update);
    setTimeout(update, 300);

    if (window.ResizeObserver) {
      var observer = new ResizeObserver(update);
      observer.observe(img);
      observer.observe(ancestor);
    } else {
      window.addEventListener('resize', update);
    }
  }

  function attachAsWrapper(img, key) {
    var computedDisplay = window.getComputedStyle(img).display;
    var isInline = computedDisplay.indexOf('inline') === 0;

    var wrapper = document.createElement('span');
    wrapper.className = 'ai-label-wrap ' + (isInline ? 'ai-label-wrap--inline' : 'ai-label-wrap--block');
    wrapper.style.position = 'relative';
    wrapper.style.display = isInline ? 'inline-block' : 'block';

    img.parentNode.insertBefore(wrapper, img);
    wrapper.appendChild(img);

    var badge = createBadge(key);
    badge.style.position = 'absolute';
    badge.style.right = GUTTER_PX + 'px';
    badge.style.bottom = GUTTER_PX + 'px';

    var update = function () {
      var imgRect = img.getBoundingClientRect();
      if (imgRect.width === 0) return;
      badge.style.width = clamp(imgRect.width * WIDTH_RATIO, MIN_PX, MAX_PX) + 'px';
    };

    wrapper.appendChild(badge);
    update();

    if (window.ResizeObserver) {
      new ResizeObserver(update).observe(img);
    } else {
      window.addEventListener('resize', update);
    }
  }

  function processImage(img) {
    if (!img || img.classList.contains('ai-label-badge') || img.dataset.aiLabelProcessed) {
      return;
    }
    img.dataset.aiLabelProcessed = 'true';

    var src = img.currentSrc || img.getAttribute('src') || '';
    var key = getLabelKey(src);
    if (!key) return;

    var ancestor = getPositionedAncestor(img);

    if (!ancestor) {
      var imgPosition = window.getComputedStyle(img).position;
      if ((imgPosition === 'absolute' || imgPosition === 'fixed') && img.parentElement) {
        // The image relies on being absolutely positioned to fill its
        // parent (the theme's ".img-fit" convention). Wrapping it would
        // collapse that parent's auto height, so instead make the
        // parent itself the positioning context, same as when an
        // already-positioned ancestor is found.
        img.parentElement.style.position = 'relative';
        ancestor = img.parentElement;
      }
    }

    if (ancestor) {
      attachToAncestor(img, ancestor, key);
    } else {
      attachAsWrapper(img, key);
    }
  }

  function scan(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    var images = root.querySelectorAll('img:not(.ai-label-badge)');
    images.forEach(processImage);

    if (root.tagName === 'IMG' && !root.classList.contains('ai-label-badge')) {
      processImage(root);
    }
  }

  function init() {
    scan(document);

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (node.tagName === 'IMG') {
            processImage(node);
          } else {
            scan(node);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
